import styled, { createGlobalStyle } from 'styled-components';
import { IThemeProvider, ITheme } from './theme';

export const GlobalStyle = createGlobalStyle`
  body {
    -webkit-font-smoothing: antialiased;
    font-family: Roboto, sans-serif;
    font-style: normal;
    color: ${({ theme }: IThemeProvider) => theme.colors.neutral.primary[100]};
    margin: 0;
    background: ${({ theme }: IThemeProvider) => theme.colors.neutral.secondary};
  }

  a {
    text-decoration: none; /* Remove underline for links */
  }
  
  .recharts-polar-grid-concentric-polygon {
    display: none;
  }
`;

// ### Elements for extend ###

// ### DefaultTag ###
interface IDefaultTag {
  theme: ITheme;
  color: string;
  backgroundColor: string;
  borderColor: string;
}
export const DefaultTag = styled.span<IDefaultTag>`
  display: flex;
  height: 22px;
  width: fit-content;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 400;
  text-transform: lowercase;
  box-sizing: border-box;

  ${({ theme, color, backgroundColor, borderColor }: IDefaultTag) => `
    color: ${color};
    background: ${backgroundColor};
    border: 1px solid ${borderColor};
    border-radius: ${theme.layout.spacing.nano};
    line-height: ${theme.typography.sizes.mikro};
    padding: ${theme.layout.spacing.nano} ${theme.layout.spacing.mili};
  `};
`;

// ### Buttons ###
interface IDefaultButton {
  theme: ITheme;
  width?: string;
  padding?: string;
}
export const DefaultButton = styled.button<IDefaultButton>`
  height: 48px;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  gap: 8px;
  box-sizing: border-box;
  cursor: pointer;

  ${({ theme, width, padding }: IDefaultButton) => `
    width: ${width || '128px'};
    padding: ${padding || '0'};
    background: ${theme.colors.secondary[100]};
    color: ${theme.colors.neutral.primary[5]};
    font-size: 16px;
    font-weight: 700;
    line-height: 19px;
  `};
`;
interface IScrollElements {
  theme: ITheme;
  scroll?: boolean;
}

export const DarkButton = styled(DefaultButton)<IScrollElements>`
  display: flex;
  width: auto;
  height: 48px;
  border-radius: 8px;
  border: none;
  align-items: center;

  ${({ theme, scroll }: IScrollElements) => `
    background: ${theme.colors.primary[100]};
    color: ${theme.colors.neutral.primary[5]};
    transition: height 0.5s;
    padding: 0 ${theme.layout.spacing.centy};

    &:disabled {
      background: ${theme.colors.neutral.primary[20]};
      color: ${theme.colors.neutral.primary[60]};
      cursor: not-allowed;
      opacity: 0.6;

      svg {
        opacity: 0.6;
      }
    }
    
    ${
      scroll
        ? `
      height: 36px;
    `
        : ''
    }
  `}
`;

export const LightButton = styled(DefaultButton)<IScrollElements>`
  width: auto;
  height: 48px;
  border-radius: 8px;

  ${({ theme, scroll }: IScrollElements) => `
    border: 2px solid ${theme.colors.primary[100]};
    background: ${theme.colors.neutral.primary[0]};
    color: ${theme.colors.neutral.primary[100]};
    margin-right: ${theme.layout.spacing.centy};
    padding: 0 ${theme.layout.spacing.centy};
    transition: height 0.5s;
    ${
      scroll
        ? `
      height: 36px;
    `
        : ''
    }
  `}
`;

// ### Inputs ###
export interface IDefaultInput {
  theme: ITheme;
  width?: string;
  padding?: string;
  error?: boolean;
  height?: string;
}
export const DefaultInput = styled.input`
  display: flex;
  height: 48px;
  align-items: center;
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  outline: none;
  box-shadow: none;
  ${({ theme, width, padding, error, height }: IDefaultInput) => `
      background: ${theme.colors.neutral.primary[0]};
      border: 1px solid ${
        error ? theme.colors.support.orange[100] : theme.colors.neutral.primary[20]
      };
      border-radius: 8px; 
      width: ${width || '368px'};
      height ${height || '48px'};
      padding: ${padding || '0 12px'};
        ::placeholder,
        ::-webkit-input-placeholder {
          color: ${theme.colors.neutral.primary[40]};
        }
        :-ms-input-placeholder {
          color: green;
        }
  `}
  &:hover {
    ${({ theme }: IDefaultInput) => `
      border: 1px solid ${theme.colors.neutral.primary[50]};
    `}
  }
  &:focus {
    box-shadow: none;
    ${({ theme }: IDefaultInput) => `
      border: 1px solid ${theme.colors.neutral.primary[100]};
    `}
  }
  &.invalid-input {
    ${({ theme }: IDefaultInput) => `
      border: 1px solid ${theme.colors.support.orange[100]};
    `}
  }
`;

// ### Fonts ###

// # Mega Title #
export const MegaTitle = styled.span`
  font-weight: 400;
  font-size: 64px;
  line-height: 75px;
  ${({ theme }: IThemeProvider) => `
       color:  ${theme.colors.neutral.primary[100]};
  `}
`;

export const MegaTitleBold = styled(MegaTitle)`
  font-weight: 800;
`;

// # Heading #
export const HeadingExtraBoldL = styled.span`
  font-weight: 800;
  font-size: 40px;
  line-height: 47px;
`;

export const HeadingExtraBoldM = styled(HeadingExtraBoldL)`
  font-size: 32px;
  line-height: 38px;
`;

export const HeadingExtraBoldS = styled(HeadingExtraBoldL)`
  font-size: 20px;
  line-height: 23px;
`;

// # Body #
export const BodyRegularL = styled.span`
  font-weight: 400;
  font-size: 36px;
  line-height: 42px;
`;

export const BodyBoldL = styled(BodyRegularL)`
  font-weight: 700;
`;

export const BodyRegularM = styled.span`
  font-weight: 400;
  font-size: 24px;
  line-height: 28px;
`;

export const BodyBoldM = styled(BodyRegularM)`
  font-weight: 700;
`;

export const BodyRegularS = styled.span`
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
`;

export const BodyBoldS = styled(BodyRegularS)`
  font-weight: 700;
`;

// # Link #

export const LinkRegularL = styled.span`
  font-weight: 400;
  font-size: 36px;
  line-height: 42px;
  text-decoration-line: underline;
`;

export const LinkBoldL = styled(LinkRegularL)`
  font-weight: 700;
`;

export const LinkRegularM = styled(LinkRegularL)`
  font-size: 24px;
  line-height: 28px;
`;

export const LinkBoldM = styled(LinkRegularM)`
  font-weight: 700;
`;

export const LinkRegularS = styled(LinkRegularL)`
  font-size: 16px;
  line-height: 19px;
`;

export const LinkBoldS = styled(LinkRegularS)`
  font-weight: 700;
`;

// # Button #

export const ButtonBoldM = styled.span`
  font-weight: 700;
  font-size: 16px;
  line-height: 19px;
  text-transform: capitalize;
`;

// # Label #

export const LabelRegularM = styled.span`
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
`;

export const LabelRegularBoldM = styled(LabelRegularM)`
  font-weight: 700;
`;

// # Caption #

export const CaptionRegularL = styled.span`
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
`;

export const CaptionBoldL = styled(CaptionRegularL)`
  font-weight: 700;
`;

export const CaptionBoldLUppercase = styled(CaptionBoldL)`
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const CaptionRegularM = styled.span`
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
`;

export const MobileScreensCont = styled.div`
  position: relative;
  display: none;
  width: 100%;
  justify-content: flex-end;

  ${({ theme }: IThemeProvider) => `
    @media (${theme.layout.breakpoints.max.desktop}) { 
      display: flex;
    }
  `}
`;
export const DesktopScreenCont = styled.div`
  position: relative;
  display: none;
  width: 100%;
  justify-content: flex-end;

  ${({ theme }: IThemeProvider) => `
    @media (${theme.layout.breakpoints.min.desktop}) {
      display: flex;
    }
  `}
`;

export const SmallerScreensCont = styled(MobileScreensCont)`
  justify-content: flex-end;
  width: 100%;
`;
export const BiggerScreensCont = styled(DesktopScreenCont)`
  justify-content: flex-end;
  width: 100%;
`;

export const FlexEndRelCont = styled.div`
  position: relative;
  justify-content: flex-end;
  width: 100%;
`;

export const GlobalSpinnerCont = styled.div`
  position: fixed;
  z-index: 1;
  top: 50%;
  left: 50%;

  .ant-spin-dot {
    font-size: 60px;

    .ant-spin-dot-item {
      width: 23.83px;
      height: 23.83px;
    }
  }
`;

export const PageTitle = styled.h1`
  margin: 0;
  font-weight: 800;
  font-size: 28px;
  line-height: 34px;
  letter-spacing: -0.01em;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.neutral.primary[100]};
  `}
`;

export const PageMeta = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  gap: 6px 20px;
  font-size: 14px;
  line-height: 20px;
  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.neutral.primary[70]};
  `}
`;

export const PageMetaItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }

  svg path {
    ${({ theme }: IThemeProvider) => `
      fill: ${theme.colors.neutral.primary[60]};
    `}
  }
`;

export const PageMetaValue = styled.span`
  font-weight: 600;
  ${({ theme }: IThemeProvider) => `
    color: ${theme.colors.neutral.primary[100]};
  `}
`;

interface IHeaderButton {
  theme: ITheme;
  $variant?: 'primary' | 'secondary';
  $compact?: boolean;
}

export const HeaderButton = styled.button<IHeaderButton>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-sizing: border-box;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s, height 0.3s;

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  ${({ theme, $variant = 'primary', $compact }: IHeaderButton) => {
    const isPrimary = $variant === 'primary';
    return `
      height: ${$compact ? '36px' : '40px'};
      padding: 0 ${theme.layout.spacing.centy};
      color: ${isPrimary ? theme.colors.neutral.primary[0] : theme.colors.secondary[100]};
      background: ${isPrimary ? theme.colors.primary[100] : theme.colors.neutral.primary[0]};
      border: 1px solid ${isPrimary ? theme.colors.primary[100] : theme.colors.neutral.primary[20]};
      box-shadow: 0 1px 2px rgba(24, 58, 96, 0.06);

      svg path {
        fill: ${isPrimary ? theme.colors.neutral.primary[0] : theme.colors.secondary[100]};
      }
      svg g {
        stroke: ${isPrimary ? theme.colors.neutral.primary[0] : theme.colors.secondary[100]};
      }

      &:hover:not(:disabled) {
        background: ${isPrimary ? '#23709A' : theme.colors.primary[10]};
        border-color: ${isPrimary ? '#23709A' : theme.colors.primary[30]};
      }

      &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 3px ${theme.colors.primary[30]};
      }

      &:disabled {
        background: ${theme.colors.neutral.primary[10]};
        border-color: ${theme.colors.neutral.primary[10]};
        color: ${theme.colors.neutral.primary[50]};
        cursor: not-allowed;
        box-shadow: none;

        svg path { fill: ${theme.colors.neutral.primary[50]}; }
        svg g { stroke: ${theme.colors.neutral.primary[50]}; }
      }
    `;
  }}
`;

export const PageTabsCont = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 28px;
  width: 100%;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  ${({ theme }: IThemeProvider) => `
    border-bottom: 1px solid ${theme.colors.neutral.primary[20]};
  `}
`;

interface IPageTab {
  theme: ITheme;
  $isActive?: boolean;
}

export const PageTab = styled.button<IPageTab>`
  position: relative;
  flex-shrink: 0;
  margin: 0;
  padding: 10px 0 12px;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: 15px;
  line-height: 20px;
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.15s;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 2px;
    border-radius: 2px 2px 0 0;
    transition: background 0.15s;
  }

  ${({ theme, $isActive }: IPageTab) => `
    color: ${$isActive ? theme.colors.secondary[100] : theme.colors.neutral.primary[70]};
    font-weight: ${$isActive ? 600 : 400};

    &::after {
      background: ${$isActive ? theme.colors.primary[100] : 'transparent'};
    }

    &:hover {
      color: ${theme.colors.secondary[100]};
    }
    &:hover::after {
      background: ${$isActive ? theme.colors.primary[100] : theme.colors.neutral.primary[30]};
    }

    &:focus-visible {
      outline: none;
      color: ${theme.colors.secondary[100]};
      box-shadow: inset 0 -2px 0 ${theme.colors.primary[70]};
    }
  `}
`;
